/* eslint-disable max-lines */
import { ApolloProvider } from '@apollo/client';
import { AbacRuleName } from '@jupiterone/jupiter-types';
import {
  ApolloClientWithSnap,
  commonCacheConfig,
  createSessionManager,
  ErrorBoundary,
  FeatureFlagsProvider,
  hasAccount,
  initPendo,
  J1ApolloClient,
  LogoProgress,
  polyfillFontLoading,
  SessionContext,
  SnackbarNotificationProvider,
  ConfirmationPromptProvider,
  ThemeWrap,
  useAsyncEffect,
  JupiterTheme,
  useSnackbarNotification,
  ONBOARDING_V2_PATH,
  useFeatureFlags,
} from '@jupiterone/jupiter-web-toolkit';
import * as Sentry from '@sentry/react';
import React, { useEffect, useLayoutEffect, useState } from 'react';
import { BreadcrumbsProvider } from 'react-breadcrumbs-context';
import { BrowserRouter } from 'react-router-dom';
import { ISession } from '../types';
import { getAccountIdFromUrl } from '../util/url';
import { login } from '../auth/loginFlows';
import { parseAuthQuery } from '../auth/query';
import {
  makeStyles,
  createStyles,
  createGenerateClassName,
  StylesProvider,
  ThemeProviderProps,
} from '@material-ui/core';
import { UserProvider } from '@jupiterone/jupiter-web-toolkit/src/api/fetch/useUser';
import { ABACProvider } from '@jupiterone/jupiter-web-toolkit/src/api/fetch/useABAC';
import {
  AccountsProvider,
  useAccounts,
} from '@jupiterone/jupiter-web-toolkit/src/api/fetch/useAccounts';

const useStyles = makeStyles((theme: JupiterTheme) =>
  createStyles({
    '@keyframes fadeIn': {
      from: {
        opacity: 0,
      },
      to: {
        opacity: 1,
      },
    },
    loadingIconContainer: {
      animation: '$fadeIn 0.25s',
      display: 'flex !important',
      alignItems: 'center',
      justifyContent: 'center',
      height: '100vh',
    },
  })
);

interface Props {
  children: any;
  cacheConfig?: any;
  pendoProperties?: any;
  jssPrefix?: string;
  routePrefix?: string;
  isSessionEmitter?: boolean;
  omitBrowserRouter?: boolean;
  theme?: ThemeProviderProps['theme'];
}

/**
 * A provider component that should only be rendered if a session has been
 * successfully fetched
 */
const AppAPIProvider = ({
  routePrefix,
  children,
  isSessionEmitter,
  pendoProperties,
  cacheConfig,
  omitBrowserRouter,
}: Props) => {
  const notification = useSnackbarNotification();
  const classes = useStyles();
  const [client, setClient] = useState<ApolloClientWithSnap | undefined>();

  useLayoutEffect(() => {
    const head = document.getElementsByTagName('head');
    const child = document.createElement('link');
    child.setAttribute(
      'href',
      'https://jupiterone.com/app/uploads/2021/02/favicon.png'
    );
    child.setAttribute('rel', 'icon');
    child.setAttribute('sizes', '32x32');
    head[0].appendChild(child);
  });

  // ALL apps will wait until the client is broadcasted
  // Root apps eat their own dog food
  useEffect(() => {
    const interval = setInterval(() => {
      window.dispatchEvent(new Event('clientRequest'));
    }, 250);
    // This is always listening and will update if the emitter updates the client
    window.addEventListener(
      'receiveClient',
      ({ detail }: any) => {
        const client = detail as ApolloClientWithSnap | undefined;
        if (client) {
          clearInterval(interval);
          setClient(client);
          J1ApolloClient.setShared(client);
        }
      },
      false
    );
  }, []);

  // Load fonts
  useAsyncEffect(async () => {
    await polyfillFontLoading('Lato', [
      { weight: 300 },
      { weight: 400 },
      { weight: 700 },
      { style: 'italic' },
    ]);
  }, []);

  // Build client if isSessionEmitter is true on mount
  // Only the top most app should fetch the session, it will then send it, through the client, to the other apps as they request it
  // The above is true for now but perhaps this should be in its own module independent of arbitrarily first rendered apps ~sivli
  useEffect(() => {
    if (!isSessionEmitter) return;
    const buildClient = async () => {
      const sessionManager = createSessionManager(undefined);
      const sessionQuery = parseAuthQuery();

      let session: any; // Any for backwards compatibility

      const accountId = getAccountIdFromUrl();
      if (accountId === 'j1auth') {
        // J1 authorization code flow
        session = await login(accountId, sessionQuery.code, sessionManager);

        if (session) {
          // Temporary, for backwards compatibility
          sessionManager._setSessionInStorage(session);
          // TODO US3318 refresh token
        }
      } else {
        // Old auth flow
        session = await sessionManager.getSessionState(
          sessionQuery.code,
          sessionQuery.clientId
        );
      }

      // No session was found and we are waiting to be redirected
      if (!session) {
        return;
      }

      session.logout = sessionManager.logout.bind(sessionManager);
      session.useAccount = sessionManager.useAccount.bind(sessionManager);
      session.enforce = (operation: AbacRuleName) =>
        sessionManager?.enforce(operation, session.policy);

      if (hasAccount(session)) {
        // since we have the session now, let's make sure sentry has the correct account/user data
        Sentry.configureScope((scope) => {
          const localSession = session as ISession;
          // Set the accountId in a tag because arbitrary key-value pairs not defined on the Sentry
          // user object are not indexable or searchable.  Adding accountId as a tag lets us search it
          // in the Sentry UI
          const accountName = localSession.account?.name || 'unknown';
          scope.setTag('accountName', accountName);
          const accountId = localSession.account?.id || 'unknown';
          scope.setTag('accountId', accountId);

          // Set this as no for now, and anyone that sends an error notification event can set it to yes
          scope.setTag('SnackbarNotificationError', 'no');
        });

        Sentry.setContext('userPolicy', session.policy);

        try {
          initPendo(session, pendoProperties);
        } catch (err) {
          console.error(err);
        }
      } else {
        // user needs to go to onboarding
        if (window.location.href.indexOf(ONBOARDING_V2_PATH) === -1) {
          const { protocol, host } = window.location;
          window.location.href = `${protocol}//${host}${ONBOARDING_V2_PATH}`;
        }
      }
      const newClient = new ApolloClientWithSnap({
        cacheConfig: { ...commonCacheConfig, cacheConfig },
        session,
        onError: (err: any) => {
          if (!err?.length) return;
          const { code, path, message, statusCode } = err[0];

          if (statusCode === 401) {
            return;
          }

          if (code && message) {
            if (
              code === 'ALREADY_EXECUTING_ERROR' &&
              path[0] === 'invokeIntegrationInstance'
            ) {
              notification.warning(message);
              return;
            }
          }

          notification.error({
            message:
              message || 'An unexpected error occurred! Please try again.',
            error: err[0],
          });
        },
      });
      sessionManager._client = newClient;

      // share session with the ALL apps
      window.addEventListener('clientRequest', () =>
        window.dispatchEvent(
          new CustomEvent('receiveClient', { detail: newClient })
        )
      );
    };
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    void buildClient();
  }, []);

  if (client?.session?.accessToken) {
    const nonBrowserRouterJSX = (
      <ApolloProvider client={client}>
        <SessionContext.Provider value={client.session}>
          <AccountsProvider>
            <ABACProvider>
              <UserProvider>
                <FeatureFlagsProvider>
                  <FinalLoader>{children}</FinalLoader>
                </FeatureFlagsProvider>
              </UserProvider>
            </ABACProvider>
          </AccountsProvider>
        </SessionContext.Provider>
      </ApolloProvider>
    );

    return omitBrowserRouter ? (
      nonBrowserRouterJSX
    ) : (
      <BrowserRouter basename={routePrefix}>
        {nonBrowserRouterJSX}
      </BrowserRouter>
    );
  }

  return (
    <div className={classes.loadingIconContainer}>
      <LogoProgress />
    </div>
  );
};

// This is temporary to Prevent permission denied flashing on screen
// Will be removed with the last of the 401 work ~Sivli
const FinalLoader = ({ children }: { children: any }) => {
  const classes = useStyles();
  const { loading } = useAccounts();
  const featureFlags = useFeatureFlags();
  if (loading || featureFlags.loading)
    return (
      <div className={classes.loadingIconContainer}>
        <LogoProgress />
      </div>
    );
  return children;
};

const AppProviders = ({ jssPrefix, theme, ...props }: Props) => {
  const generator = createGenerateClassName({
    seed: jssPrefix || '',
    productionPrefix: jssPrefix || 'jss',
  });

  return (
    <StylesProvider generateClassName={generator}>
      <ThemeWrap theme={theme}>
        <>
          <ErrorBoundary>
            <SnackbarNotificationProvider>
              <ConfirmationPromptProvider>
                <BreadcrumbsProvider>
                  <AppAPIProvider {...props} />
                </BreadcrumbsProvider>
              </ConfirmationPromptProvider>
            </SnackbarNotificationProvider>
          </ErrorBoundary>
          <div id="jupiterone-notification-container" />
        </>
      </ThemeWrap>
    </StylesProvider>
  );
};

export default AppProviders;
